import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAlert, useCreateSilence, useAckAlert } from '@/lib/alerts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { SeverityBadge } from '@/components/SeverityBadge'

const DURATIONS: Array<{ label: string; seconds: number }> = [
  { label: '1h', seconds: 3600 },
  { label: '4h', seconds: 4 * 3600 },
  { label: '24h', seconds: 24 * 3600 },
  { label: '7d', seconds: 7 * 24 * 3600 },
]

export function AlertDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useAlert(id)
  const silenceMut = useCreateSilence()
  const ackMut = useAckAlert()

  async function silence(seconds: number) {
    if (!data) return
    try {
      await silenceMut.mutateAsync({
        fingerprint: data.fingerprint,
        duration_seconds: seconds,
        reason: 'manual silence',
      })
      toast.success(`Silenced for ${seconds / 3600}h`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Silence failed')
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/alerts" className="text-sm text-muted-foreground hover:underline">
          ← Back to alerts
        </Link>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          {data && <SeverityBadge severity={data.severity} />}
          {data?.title ?? id}
        </h1>
      </div>

      {isLoading && <Skeleton className="h-48" />}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Summary</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div>Rule: <span className="text-muted-foreground">{data.rule_name}</span></div>
              <div>Opened at: <span className="text-muted-foreground">{new Date(data.opened_at).toLocaleString()}</span></div>
              <div>Last seen: <span className="text-muted-foreground">{new Date(data.last_seen_at).toLocaleString()}</span></div>
              {data.closed_at && (
                <div>Closed at: <span className="text-muted-foreground">{new Date(data.closed_at).toLocaleString()}</span></div>
              )}
              {data.entity_name && (
                <div>Entity: <span className="text-muted-foreground">{data.entity_name} ({data.entity_kind})</span></div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
                {JSON.stringify(data.details, null, 2)}
              </pre>
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Silence this fingerprint</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {DURATIONS.map((d) => (
                <Button
                  key={d.seconds}
                  variant="outline"
                  size="sm"
                  disabled={silenceMut.isPending}
                  onClick={() => void silence(d.seconds)}
                >
                  {d.label}
                </Button>
              ))}
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Acknowledge</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.acknowledged_at ? (
                <p className="text-sm text-muted-foreground">
                  Acknowledged at {new Date(data.acknowledged_at).toLocaleString()}
                  {data.acknowledged_by && ` by ${data.acknowledged_by}`}
                </p>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={ackMut.isPending}
                  onClick={() =>
                    ackMut.mutate(data.id, {
                      onSuccess: () => toast.success('Alert acknowledged'),
                      onError: (e) => toast.error(e instanceof Error ? e.message : 'ACK failed'),
                    })
                  }
                >
                  Acknowledge
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
